/**
 * Command-palette domain types.
 *
 * The palette is fed a flat list of `Command`s by `useCommands()`. Each
 * command carries its display copy, a category for grouping, optional
 * keyboard shortcut hint, and an `action` that fires on Enter / click.
 *
 * Commands are computed per-render — they read from React Query (recent
 * sessions), the project store, and route info. The list is small
 * (typically 20–60 items) so re-computing on every keystroke is cheap.
 */
import type { ReactNode } from "react";

export type CommandCategory = "navigate" | "action" | "recent" | "project";

export interface Command {
  /** Stable identity — drives React keys and search-result memoisation. */
  id: string;
  /** Primary label rendered in mono. */
  label: string;
  /** Optional secondary line (relative time, project counts, etc). */
  hint?: string;
  /** Optional ⌘ K-style shortcut hint shown right-aligned. */
  shortcut?: string;
  /** Category for grouping in the rendered list. */
  category: CommandCategory;
  /** Optional 16px icon. */
  icon?: ReactNode;
  /** Fires on Enter / click. Closes the palette implicitly via the
   *  caller's handler — the action shouldn't dismiss itself. */
  action: () => void;
  /** Free-form keywords that contribute to fuzzy-match scoring even
   *  if they aren't visible in `label` / `hint`. Useful for aliasing
   *  ("preferences" → settings, "logout" → sign out). */
  keywords?: string[];
}

export const CATEGORY_LABEL: Record<CommandCategory, string> = {
  navigate: "Navigate",
  action: "Actions",
  recent: "Recent",
  project: "Projects",
};

/** Display order for category sections. */
export const CATEGORY_ORDER: CommandCategory[] = [
  "navigate",
  "action",
  "project",
  "recent",
];
